import { useEffect, useMemo, useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import "../style/addresspage.css";
import {
  getSelectedAddressId,
  loadAddresses,
  saveAddresses,
  setSelectedAddressId,
} from "../services/addressService";
import { getCartSummary, loadCart } from "../services/cartService";

export default function Address() {
  const navigate = useNavigate();
  const [addresses, setAddresses] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [editId, setEditId] = useState(null);

  const emptyForm = {
    name: "",
    phone: "",
    pincode: "",
    address1: "",
    address2: "",
    city: "",
    state: "",
    type: "Home",
  };

  const [formData, setFormData] = useState(emptyForm);
  const cartItems = useMemo(() => loadCart(), []);
  const summary = useMemo(() => getCartSummary(cartItems), [cartItems]);

  useEffect(() => {
    const savedAddresses = loadAddresses();
    const currentSelectedId = getSelectedAddressId();

    setAddresses(savedAddresses);

    if (savedAddresses.length > 0) {
      const nextSelectedId =
        currentSelectedId ||
        String(savedAddresses.find((address) => address.isDefault)?.id || savedAddresses[0].id);
      setSelectedId(nextSelectedId);
      setSelectedAddressId(nextSelectedId);
    }
  }, []);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((previous) => ({ ...previous, [name]: value }));
  };

  const resetForm = () => {
    setFormData(emptyForm);
    setEditId(null);
    setShowForm(false);
  };

  const handleSubmit = (event) => {
    event.preventDefault();

    if (!formData.name || !formData.phone || !formData.pincode || !formData.address1 || !formData.city || !formData.state) {
      alert("Please fill all required fields");
      return;
    }

    let updatedAddresses = [];

    if (editId) {
      updatedAddresses = addresses.map((address) =>
        String(address.id) === String(editId) ? { ...address, ...formData } : address
      );
    } else {
      const newAddress = {
        id: Date.now(),
        ...formData,
        isDefault: addresses.length === 0,
      };
      updatedAddresses = [...addresses, newAddress];
      setSelectedId(String(newAddress.id));
      setSelectedAddressId(newAddress.id);
    }

    setAddresses(saveAddresses(updatedAddresses));
    resetForm();
  };

  const handleEdit = (address) => {
    setFormData({
      name: address.name,
      phone: address.phone,
      pincode: address.pincode,
      address1: address.address1,
      address2: address.address2,
      city: address.city,
      state: address.state,
      type: address.type,
    });
    setEditId(address.id);
    setShowForm(true);
  };

  const handleDelete = (id) => {
    if (!window.confirm("Delete this address?")) {
      return;
    }

    const updatedAddresses = addresses.filter((address) => String(address.id) !== String(id));
    setAddresses(saveAddresses(updatedAddresses));

    if (String(selectedId) === String(id)) {
      const nextSelectedId = updatedAddresses[0] ? String(updatedAddresses[0].id) : null;
      setSelectedId(nextSelectedId);
      setSelectedAddressId(nextSelectedId);
    }
  };

  const selectAddress = (id) => {
    const normalizedId = String(id);
    setSelectedId(normalizedId);
    setSelectedAddressId(normalizedId);
  };

  return (
    <main className="address-container">
      <nav className="breadcrumb-nav">
        <NavLink to="/home" className="breadcrumb-item">Home</NavLink>
        <span className="breadcrumb-separator">{">"}</span>
        <NavLink to="/add-to-cart" className="breadcrumb-item">Cart</NavLink>
        <span className="breadcrumb-separator">{">"}</span>
        <span className="breadcrumb-item active-page">Address</span>
      </nav>

      <section className="checkout-hero">
        <div>
          <p className="checkout-eyebrow">Checkout Step 2</p>
          <h1>Select Shipping Address</h1>
          <p>Choose a saved address or add a new one before payment.</p>
        </div>
        <div className="checkout-step-pill">Address - Payment - Order Success</div>
      </section>

      <div className="address-options-panel">
        <h2>Your Saved Addresses ({addresses.length})</h2>

        {summary.items === 0 && (
          <div className="empty-address-message">
            Your cart is empty. Add products first so you can complete checkout.
          </div>
        )}

        <div className="saved-addresses-list">
          {addresses.length === 0 && (
            <p className="empty-address-message">No saved addresses found. Add one to continue checkout.</p>
          )}

          {addresses.map((address) => (
            <div
              key={address.id}
              className={`address-card ${String(selectedId) === String(address.id) ? "active" : ""}`}
              onClick={() => selectAddress(address.id)}
            >
              <span className="address-type">{address.type}</span>

              <div className="address-details">
                <strong>
                  {address.name} - {address.phone}
                </strong>
                <p>
                  {address.address1}
                  {address.address2 ? `, ${address.address2}` : ""}
                </p>
                <p>
                  {address.city}, {address.state} - {address.pincode}
                </p>
              </div>

              <div className="address-actions">
                <button
                  className="edit-btn"
                  onClick={(event) => {
                    event.stopPropagation();
                    handleEdit(address);
                  }}
                >
                  Edit
                </button>

                <button
                  className="delete-btn"
                  onClick={(event) => {
                    event.stopPropagation();
                    handleDelete(address.id);
                  }}
                >
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>

        <button className="secondary-btn" onClick={() => setShowForm((value) => !value)}>
          {showForm ? "Cancel" : "Add New Address"}
        </button>

        {showForm && (
          <div className="new-address-form-container">
            <h3>{editId ? "Update Address" : "Enter New Shipping Address"}</h3>

            <form onSubmit={handleSubmit}>
              <div className="address-form-grid">
                <div>
                  <label className="form-level">Full Name *</label>
                  <input name="name" placeholder="Name *" required value={formData.name} onChange={handleChange} />
                </div>
                <div>
                  <label className="form-level">Phone Number *</label>
                  <input name="phone" placeholder="Phone" required value={formData.phone} onChange={handleChange} />
                </div>
                <div>
                  <label className="form-level">Pincode *</label>
                  <input name="pincode" placeholder="Pincode" required value={formData.pincode} onChange={handleChange} />
                </div>
                <div className="full-width">
                  <label className="form-level">Address Line 1 *</label>
                  <input
                    name="address1"
                    placeholder="Address Line 1"
                    required
                    value={formData.address1}
                    onChange={handleChange}
                  />
                </div>
                <div className="full-width">
                  <label className="form-level">Address Line 2</label>
                  <input name="address2" placeholder="Address Line 2" value={formData.address2} onChange={handleChange} />
                </div>
                <div>
                  <label className="form-level">City *</label>
                  <input name="city" placeholder="City" required value={formData.city} onChange={handleChange} />
                </div>
                <div>
                  <label className="form-level">State *</label>
                  <input name="state" placeholder="State" required value={formData.state} onChange={handleChange} />
                </div>
              </div>

              <div className="address-type-group">
                <label>
                  <input type="radio" name="type" value="Home" checked={formData.type === "Home"} onChange={handleChange} /> Home
                </label>
                <label>
                  <input type="radio" name="type" value="Work" checked={formData.type === "Work"} onChange={handleChange} /> Work
                </label>
              </div>

              <button className="primary-btn" type="submit">
                Save Address
              </button>
            </form>
          </div>
        )}

        <div className="cart-summary-panel" style={{ marginTop: "24px" }}>
          <h2>Order Summary</h2>
          <div className="summary-line">
            <span>Items</span>
            <span>{summary.items}</span>
          </div>
          <div className="summary-line">
            <span>Subtotal</span>
            <span>Rs.{summary.subtotal.toLocaleString("en-IN")}</span>
          </div>
          <div className="summary-line">
            <span>Discount</span>
            <span>- Rs.{summary.discount.toLocaleString("en-IN")}</span>
          </div>
          <div className="summary-line total-payable">
            <span>Total</span>
            <span>Rs.{summary.totalPayable.toLocaleString("en-IN")}</span>
          </div>
        </div>

        <button className="primary-btn proceed-btn" disabled={!selectedId || summary.items === 0} onClick={() => navigate("/payment")}>
          Deliver to this Address & Proceed
        </button>
      </div>
    </main>
  );
}
